<template>
  <UFooter>
    <!-- Top Section with Columns -->
    <template #top>
      <UContainer>
        <UFooterColumns :columns="footerColumns" :ui="{
          root: 'lg:grid lg:grid-cols-2 lg:gap-8',
          center: 'sm:grid sm:grid-cols-2',
          right: '',
          list: 'text-center sm:text-left',
          item: 'flex justify-center sm:justify-start',
        }">
          <!-- Newsletter in the right slot -->
          <template #right>
            <div class="max-w-sm mx-auto  text-center sm:text-left">
              <h6 class="font-semibold text-gray-900 dark:text-gray-100 mb-4">Stay Connected</h6>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Subscribe to our newsletter for exclusive deals and updates.</p>
              <form @submit.prevent="subscribeNewsletter" class="space-y-3">
                <div class="flex gap-2 text-sm">
                  <UCheckbox label="Women" id="newsletter-women" v-model="newsletterPrefs.women" />
                  <UCheckbox label="Men" id="newsletter-men" v-model="newsletterPrefs.men" />
                </div>
                <div class="flex gap-2">
                  <UInput
                    v-model="newsletterEmail"
                    type="email"
                    placeholder="Enter your email"
                    class="flex-1"
                    required
                  />
                  <UButton type="submit" color="primary">
                    <Icon name="i-heroicons-arrow-up-right" class="w-4 h-4" />
                  </UButton>
                </div>
              </form>

              <!-- Social Links -->
              <div class="flex justify-center gap-2 mt-6">
                <UButton variant="link" color="neutral" size="sm" to="#" aria-label="Facebook">
                  <Icon name="i-simple-icons-facebook" class="w-5 h-5" />
                </UButton>
                <UButton variant="link" color="neutral" size="sm" to="#" aria-label="Instagram">
                  <Icon name="i-simple-icons-instagram" class="w-5 h-5" />
                </UButton>
                <UButton variant="link" color="neutral" size="sm" to="#" aria-label="Tiktok">
                  <Icon name="i-simple-icons-tiktok" class="w-5 h-5" />
                </UButton>
                <UButton variant="link" color="neutral" size="sm" to="#" aria-label="YouTube">
                  <Icon name="i-simple-icons-youtube" class="w-5 h-5" />
                </UButton>
              </div>
            </div>
          </template>
        </UFooterColumns>
      </UContainer>
    </template>

    <!-- Bottom Section with Copyright and Links -->
    <template #left>
      <div class="flex flex-col md:flex-row items-center gap-2 text-sm text-muted">
        <span>© {{ new Date().getFullYear() }} {{ $t('All rights reserved') }}.</span>
        <div class="hidden md:block w-px h-4 bg-gray-300" />
        <div class="flex gap-4">
          <ULink :to="$localePath({ name: 'privacy-policy' })" class="hover:text-default">{{ $t('Privacy') }}</ULink>
          <ULink :to="$localePath({ name: 'terms' })" class="hover:text-default">{{ $t('Terms') }}</ULink>
        </div>
      </div>
    </template>

    <!-- Payment Methods -->
    <template #right>
      <div class="flex items-center gap-2">
        <span class="text-sm text-muted mr-2">{{ $t('We accept') }}:</span>
        <div class="flex gap-2">
          <div class="w-auto h-8 px-2 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
            <img 
              :src="$colorMode.value === 'dark' ? '/img/payment-methods/visa-dark-mode.svg' : '/img/payment-methods/visa-light-mode.svg'" 
              alt="Visa" 
              class="h-5" 
            />
          </div>
          <div class="w-auto h-8 px-2 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
            <img 
              src="/img/payment-methods/mastercard.svg" 
              alt="MasterCard" 
              class="h-5" 
            />
          </div>
          <div class="w-auto h-8 px-2 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
            <img 
              :src="$colorMode.value === 'dark' ? '/img/payment-methods/paypal-dark-mode.svg' : '/img/payment-methods/paypal-light-mode.svg'" 
              alt="PayPal" 
              class="h-5" 
            />
          </div>
          <div class="w-auto h-8 px-2 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
            <img 
              :src="$colorMode.value === 'dark' ? '/img/payment-methods/google-pay-dark-mode.svg' : '/img/payment-methods/google-pay-light-mode.svg'" 
              alt="Google Pay" 
              class="h-5" 
            />
          </div>
        </div>
      </div>
    </template>
  </UFooter>
</template>

<script setup lang="ts">
const { t: $t } = useI18n()

import { ref } from 'vue'
import type { FooterColumn } from '@nuxt/ui'

const newsletterEmail = ref('')
const newsletterPrefs = ref({
  women: true,
  men: false
})

const localePath = useLocalePath()

const footerColumns: FooterColumn[] = [
  {
    label: $t('Shop'),
    children: [
      {
        label: $t('Categories'),
        to: localePath({ name: 'categories' })
      }
    ]
  },
  {
    label: $t('Account'),
    children: [
      {
        label: $t('My account'),
        to: localePath({ name: 'account' })
      },
      {
        label: $t('FAQ'),
        to: localePath({ name: 'faq' })
      }
    ]
  },
  {
    label: $t('Customer Service'),
    children: [
      {
        label: $t('About'),
        to: localePath({ name: 'about' })
      },
      {
        label: $t('Contact'),
        to: localePath({ name: 'contact' })
      },
      {
        label: $t('Terms & conditions'),
        to: localePath({ name: 'terms' })
      },
      {
        label: $t('Privacy policy'),
        to: localePath({ name: 'privacy-policy' })
      }
    ]
  }
]

function subscribeNewsletter() {
  // TODO: Implement newsletter subscription
  console.log('Newsletter subscription:', { email: newsletterEmail.value, prefs: newsletterPrefs.value })
  newsletterEmail.value = ''
}
</script>
